import { useEffect, useRef, useState, useCallback } from "react";
import { useEventTracking } from "@/hooks/useEventTracking";
import { X } from "lucide-react";

interface Banner {
  id: string;
  image_url: string;
  target_url: string;
  campaign_name: string;
  company_name: string;
}

interface BannerAdProps {
  banner: Banner;
  onDismiss?: (bannerId: string) => void;
}

const DISMISSED_KEY = "napihami_dismissed_banners";

export function getDismissedBannerIds(): string[] {
  try {
    return JSON.parse(localStorage.getItem(DISMISSED_KEY) || "[]");
  } catch {
    return [];
  }
}

export function BannerAd({ banner, onDismiss }: BannerAdProps) {
  const { trackBannerEvent } = useEventTracking();
  const hasTrackedImpression = useRef(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!hasTrackedImpression.current) {
      trackBannerEvent(banner.id, "impression");
      hasTrackedImpression.current = true;
    }
  }, [banner.id, trackBannerEvent]);

  const handleClick = () => {
    trackBannerEvent(banner.id, "click");
  };

  const handleDismiss = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const ids = getDismissedBannerIds();
    if (!ids.includes(banner.id)) {
      ids.push(banner.id);
      localStorage.setItem(DISMISSED_KEY, JSON.stringify(ids));
    }
    trackBannerEvent(banner.id, "permanent_dismiss");
    setDismissed(true);
    onDismiss?.(banner.id);
  }, [banner.id, trackBannerEvent, onDismiss]);

  if (dismissed) return null;

  return (
    <div className="relative group">
      <a
        href={banner.target_url}
        target="_blank"
        rel="noopener noreferrer"
        className="block w-full overflow-hidden"
        aria-label={`${banner.company_name} - ${banner.campaign_name}`}
        onClick={handleClick}
      >
        <img
          src={banner.image_url}
          alt={`${banner.company_name} hirdetés`}
          className="w-full h-auto"
          loading="lazy"
        />
      </a>
      <button
        onClick={handleDismiss}
        className="absolute top-1 right-1 flex items-center gap-1 bg-black/50 hover:bg-black/70 text-white rounded px-1.5 py-0.5 transition-colors"
        style={{ fontSize: "10px" }}
        aria-label="Ne mutasd többet ezt a hirdetést"
      >
        <X className="h-3 w-3" />
        <span>Ne mutasd többet</span>
      </button>
      <span
        className="absolute bottom-1 right-1 pointer-events-none select-none"
        style={{ fontSize: "7px", color: "#636363", opacity: 0.7 }}
      >
        Hirdetés
      </span>
    </div>
  );
}
